import { useContext, useState, useRef, useEffect } from "react";
import AdminLeloContext from "../../context/adminLelo/adminLeloContext";
import * as FaIcons from "react-icons/fa";

const SearchForm = ({ placeholderText }) => {
  const adminLeloContext = useContext(AdminLeloContext);
  const { getLeloEntries, appliedFilters, pageSize, setCurrentPage, searchText: contextSearchText } = adminLeloContext;

  const [searchText, setSearchText] = useState(contextSearchText || "");
  const debounceTimer = useRef(null);
  const isInitialMount = useRef(true);
  const lastSearchRef = useRef(contextSearchText || "");

  // Sync with context searchText when it changes externally (e.g., from filters)
  useEffect(() => {
    if (contextSearchText !== lastSearchRef.current && contextSearchText !== searchText) {
      setSearchText(contextSearchText || "");
      lastSearchRef.current = contextSearchText || "";
    }
  }, [contextSearchText]);

  // Dynamic search with debouncing
  useEffect(() => {
    // Skip on initial mount - let the page handle initial load
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSearchRef.current = searchText;
      return;
    }

    // Skip if searchText hasn't actually changed
    if (searchText === lastSearchRef.current) {
      return;
    }

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      // Capture the current context values inside the timeout
      const currentAppliedFilters = adminLeloContext.appliedFilters;
      const currentPageSize = adminLeloContext.pageSize;

      // Build filters
      const filters = {
        page: 1,
        pageSize: currentPageSize || 300,
      };

      // Add existing filters from appliedFilters
      if (currentAppliedFilters) {
        if (currentAppliedFilters.status) filters.status = currentAppliedFilters.status;
        if (currentAppliedFilters.permaLink) filters.permaLink = currentAppliedFilters.permaLink;
        if (currentAppliedFilters.startDate) filters.startDate = currentAppliedFilters.startDate;
        if (currentAppliedFilters.endDate) filters.endDate = currentAppliedFilters.endDate;
        // Use learningCenterId (siteId) instead of names
        if (currentAppliedFilters.learningCenterId) filters.learningCenterId = currentAppliedFilters.learningCenterId;
      }

      // Add search text only if not empty
      if (searchText && searchText.trim() !== '') {
        filters.searchText = searchText.trim();
      }

      // Update the last search ref
      lastSearchRef.current = searchText;

      // Reset to page 1 when searching
      setCurrentPage(1);

      // Call backend - this will reload data with or without search text
      getLeloEntries(filters);
    }, 500); // 500ms debounce delay

    // Cleanup function
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchText]);

  const onChangeText = (e) => {
    const value = e.target.value;
    setSearchText(value);
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  return (
    <div className="search-form-container">
      <div className="search-input-wrapper">
        <input
          type="text"
          name="lelo-search"
          className="search-input"
          placeholder={placeholderText || "Search by name, email, phone..."}
          value={searchText}
          onChange={onChangeText}
          autoComplete="off"
        />
        {searchText && (
          <button
            className="clear-search-btn"
            onClick={handleClearSearch}
            type="button"
          >
            <FaIcons.FaTimes />
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchForm;
